import { BaseAvailabilityStatus, type ActivityPlan } from '@linktivity/ars-neo';
import type { UnitCardUnit } from '@link/share/business';
import {
  applyPlanDateChange,
  clearPlanTime,
  createDefaultPlanState,
  type PlanState
} from '@link/share/hooks/planState';
import {
  findFirstAvailablePlanDay,
  findTimeAvailability,
  hasSingleIdOnlyStartTime
} from '@link/share/hooks/productPageSelectors';
import {
  findPlanInfo,
  formatTime,
  resolveStartTimeValue
} from '@link/share/utils';
import type { CalendarDay } from './calendarRange';

/*
 * The changes a plan's selection goes through, as functions from one set of
 * plan states to the next. Each returns the same Map when it changes nothing,
 * so callers can tell. From share's `useProductPage`, unchanged in behaviour;
 * there they were spread over setState callbacks and effects.
 */

export type PlanStates = Map<string, PlanState>;

const stateOf = (states: PlanStates, planId: string) =>
  states.get(planId) ?? createDefaultPlanState(planId);

/**
 * The chosen units re-priced for the plan on the date, quantities kept. Units
 * the day doesn't sell are dropped — they would be submitted at a stale
 * price. A day with no unit data leaves the selection alone.
 */
export function repriceUnits(
  units: UnitCardUnit[],
  planId: string,
  date: string,
  days: CalendarDay[]
): UnitCardUnit[] {
  // findPlanInfo falls back to the first entry for a falsy planId.
  if (!planId) return units;
  const day = days.find(item => item.date === date);
  const dayUnits = findPlanInfo(day?.planInfos, planId)?.unitItems;
  if (!dayUnits?.length) return units;
  const byId = new Map(dayUnits.map(unit => [unit.id, unit]));
  return units.flatMap(unit => {
    const dayUnit = byId.get(unit.id);
    return dayUnit
      ? [
          {
            ...unit,
            amount: dayUnit.amount ?? unit.amount,
            displayAmount: dayUnit.displayAmount ?? unit.displayAmount
          }
        ]
      : [];
  });
}

/**
 * Put the plan on the date: units re-priced for it, the slot dropped if the
 * date moved. `clearTime` drops the slot regardless — a plan taken over
 * mid-switch may sit on this date already, holding a slot from another plan's
 * inventory. An empty date changes nothing but that.
 */
export function landPlanOnDate(
  states: PlanStates,
  planId: string,
  date: string,
  clearTime: boolean,
  days: CalendarDay[]
): PlanStates {
  const current = stateOf(states, planId);
  const next =
    !date || current.selectedDate === date
      ? states
      : new Map(states).set(
          planId,
          applyPlanDateChange(
            current,
            date,
            repriceUnits(current.selectedUnits, planId, date, days)
          )
        );
  return clearTime ? clearPlanTime(next, planId) : next;
}

/**
 * The date a visitor picked, applied to the plan: units re-priced, slot
 * dropped if the date moved, and dropped regardless when `clearTime`.
 */
export function pickDate(
  states: PlanStates,
  planId: string,
  date: string,
  clearTime: boolean,
  days: CalendarDay[]
): PlanStates {
  const current = stateOf(states, planId);
  const next = new Map(states).set(
    planId,
    applyPlanDateChange(
      current,
      date,
      repriceUnits(current.selectedUnits, planId, date, days)
    )
  );
  return clearTime ? clearPlanTime(next, planId) : next;
}

/**
 * Each plan without a date gets its own first day with stock, so a plan
 * opened before the visitor picks a date lands on its inventory.
 */
export function seedFirstDates(
  states: PlanStates,
  plans: ActivityPlan[],
  days: CalendarDay[]
): PlanStates {
  let next = states;
  for (const plan of plans) {
    const planId = plan.id;
    if (!planId || states.get(planId)?.selectedDate) continue;
    const date = findFirstAvailablePlanDay(days, planId)?.date;
    if (!date) continue;
    if (next === states) next = new Map(states);
    next.set(planId, { ...stateOf(states, planId), selectedDate: date });
  }
  return next;
}

/**
 * Open-date and lottery plans pick their own date and slot: the first day
 * with stock, its first start time — for a lottery, its first open one.
 * Fills only what is missing.
 */
export function autoPickDateAndTime(
  states: PlanStates,
  plan: ActivityPlan,
  isLottery: boolean,
  days: CalendarDay[]
): PlanStates {
  const planId = plan.id ?? '';
  const day = findFirstAvailablePlanDay(days, planId);
  if (!planId || !day?.date) return states;
  const existing = stateOf(states, planId);
  if (existing.selectedDate && existing.selectedTimeId) return states;
  const startTime = isLottery
    ? (plan.startTimes?.find(
        st =>
          findTimeAvailability(st, day.timeAvailabilities)?.status ===
          BaseAvailabilityStatus.AvailabilityStatusOk
      ) ?? plan.startTimes?.[0])
    : plan.startTimes?.[0];
  return new Map(states).set(planId, {
    ...existing,
    selectedDate: existing.selectedDate || day.date,
    selectedTimeId: existing.selectedTimeId || startTime?.id || '',
    selectedTimeTitle:
      existing.selectedTimeTitle ||
      startTime?.title ||
      formatTime(startTime?.time || '', 'HH:mm') ||
      '',
    selectedTime: existing.selectedTime || resolveStartTimeValue(startTime)
  });
}

/**
 * A plan whose only start time has neither title nor time leaves nothing to
 * pick, so it is picked once the plan has a date.
 */
export function autoPickOnlyStartTime(
  states: PlanStates,
  plan: ActivityPlan
): PlanStates {
  const planId = plan.id ?? '';
  const current = states.get(planId);
  if (
    !hasSingleIdOnlyStartTime(plan) ||
    !current?.selectedDate ||
    current.selectedTimeId
  ) {
    return states;
  }
  return new Map(states).set(planId, {
    ...current,
    selectedTimeId: plan.startTimes?.[0]?.id ?? '',
    selectedTimeTitle: '',
    selectedTime: ''
  });
}

/**
 * Every plan sitting on a date in `soldOut` moved to `replacementFor(planId)`,
 * its slot dropped and its units re-priced for the new date. A plan with no
 * replacement stays — the order gate blocks it — rather than being cleared,
 * which would fight the auto-pick above.
 */
export function moveOffDates(
  states: PlanStates,
  soldOut: Set<string>,
  replacementFor: (planId: string) => string,
  days: CalendarDay[]
): PlanStates {
  let next = states;
  for (const [planId, state] of states) {
    if (!state.selectedDate || !soldOut.has(state.selectedDate)) continue;
    const replacement = replacementFor(planId);
    if (!replacement) continue;
    if (next === states) next = new Map(states);
    next.set(
      planId,
      applyPlanDateChange(
        state,
        replacement,
        repriceUnits(state.selectedUnits, planId, replacement, days)
      )
    );
  }
  return next;
}
